import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AppLayout } from '@/components/layout/AppLayout';
import PractitionerView from '@/components/views/PractitionerView';
import { medplumResourcesApi } from '@/lib/api/medplum-resources';
import { calculateAge } from '@/lib/types/patient';
import { logger } from '@/lib/utils/logger';

/**
 * PractitionerPage Component
 *
 * Displays detailed information about a specific practitioner.
 *
 * Features:
 * - Fetches practitioner data from Medplum
 * - Displays practitioner details (name, specialization, contact info)
 * - Shows status and metadata
 * - Navigate back to dashboard
 * - Edit functionality (placeholder)
 *
 * @accessibility
 * - Semantic HTML structure
 * - ARIA labels for actions
 * - Keyboard navigation support
 */
export function PractitionerPage() {
  const { practitionerId } = useParams<{ practitionerId: string }>();
  const navigate = useNavigate();

  // Fetch practitioner data
  const {
    data: practitioner,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['practitioner', practitionerId],
    queryFn: () => {
      if (!practitionerId) {
        throw new Error('Practitioner ID is required');
      }
      return medplumResourcesApi.getPractitioner(practitionerId);
    },
    enabled: !!practitionerId,
    staleTime: 60000, // 1 minute
  });

  // Transform practitioner data for view
  const practitionerData = practitioner
    ? {
        id: practitioner.id,
        name:
          practitioner.name?.[0]?.text ||
          `${practitioner.name?.[0]?.given?.join(' ') || ''} ${practitioner.name?.[0]?.family || ''}`.trim() ||
          practitioner.id,
        gender: (practitioner as any).gender,
        birthDate: (practitioner as any).birthDate
          ? formatDate((practitioner as any).birthDate)
          : undefined,
        age: (practitioner as any).birthDate
          ? calculateAge((practitioner as any).birthDate)
          : undefined,
        qualifications: extractQualifications((practitioner as any).qualification),
        email: practitioner.telecom?.find((t) => t.system === 'email')?.value,
        phone: practitioner.telecom?.find((t) => t.system === 'phone')?.value,
        status: (practitioner as any).active !== false ? 'Active' : 'Inactive',
        identifier: practitioner.identifier?.[0]?.value,
        createdAt: (practitioner as any).meta?.lastUpdated
          ? formatDateTime((practitioner as any).meta.lastUpdated)
          : undefined,
        updatedAt: (practitioner as any).meta?.lastUpdated
          ? formatDateTime((practitioner as any).meta.lastUpdated)
          : undefined,
      }
    : undefined;

  const handleBack = () => {
    navigate('/dashboard');
  };

  const handleEdit = () => {
    // TODO: Implement practitioner edit functionality
    logger.log('Edit practitioner:', practitionerId);
  };

  return (
    <AppLayout>
      <PractitionerView
        practitioner={practitionerData}
        isLoading={isLoading}
        error={
          isError
            ? error instanceof Error
              ? error.message
              : 'Failed to load practitioner details'
            : null
        }
        onBack={handleBack}
        onEdit={handleEdit}
      />
    </AppLayout>
  );
}

// Helper functions
function formatDateTime(isoString: string): string {
  try {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return isoString;
  }
}

function formatDate(dateString: string): string {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return dateString;
  }
}

interface QualificationData {
  code?: {
    coding?: Array<{
      system?: string;
      code?: string;
      display?: string;
    }>;
    text?: string;
  };
}

function extractQualifications(qualifications?: QualificationData[]) {
  if (!qualifications || qualifications.length === 0) {
    return [];
  }

  const results: Array<{ code: string; display: string; system?: string }> = [];

  qualifications.forEach((qual) => {
    if (qual.code?.coding) {
      qual.code.coding.forEach((coding) => {
        if (coding.code && coding.display) {
          results.push({
            code: coding.code,
            display: coding.display,
            system: coding.system,
          });
        }
      });
    }
  });

  return results;
}

export default PractitionerPage;
